import React, { useCallback, useEffect, useRef, useState } from "react";
import { Editor } from "react-draft-wysiwyg";
import {
	ContentState,
	EditorState,
	convertToRaw,
} from "draft-js";
import { marked } from "marked";
import htmlToDraft from "html-to-draftjs";
import draftToMarkdown from "draftjs-to-markdown";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";

const TOOLBAR = {
	options: ["inline", "blockType", "list", "link"],
	inline: {
		options: ["bold", "italic", "underline"],
	},
	blockType: {
		inDropdown: true,
		options: ["Normal", "H1", "H2", "H3"],
	},
	list: {
		options: ["unordered", "ordered"],
	},
	link: {
		options: ["link", "unlink"],
	},
};

function markdownToEditorState(markdown) {
	const md = typeof markdown === "string" ? markdown : "";
	if (!md.trim()) {
		return EditorState.createEmpty();
	}
	const html = marked.parse(md, { breaks: true });
	const { contentBlocks, entityMap } = htmlToDraft(
		typeof html === "string" ? html : String(html || "")
	);
	if (!contentBlocks || contentBlocks.length === 0) {
		return EditorState.createEmpty();
	}
	const contentState = ContentState.createFromBlockArray(
		contentBlocks,
		entityMap
	);
	return EditorState.createWithContent(contentState);
}

function editorStateToMarkdown(editorState) {
	if (!editorState) return "";
	const raw = convertToRaw(editorState.getCurrentContent());
	const md = draftToMarkdown(raw);
	return typeof md === "string" ? md.replace(/\n$/, "") : "";
}

/**
 * WYSIWYG wrapper that speaks markdown at the boundary.
 * Parent keeps markdown as source of truth for drafts / save API.
 */
export default function WritingPreviewMarkdownWysiwyg({
	value = "",
	onChange,
	disabled = false,
}) {
	const lastEmittedMdRef = useRef(null);
	const hydratingRef = useRef(false);
	const [editorState, setEditorState] = useState(() =>
		markdownToEditorState(value)
	);

	useEffect(() => {
		const incoming = typeof value === "string" ? value : "";
		// Skip rebuild when the change came from this editor (avoid cursor jumps).
		if (
			lastEmittedMdRef.current !== null &&
			incoming === lastEmittedMdRef.current
		) {
			return;
		}
		lastEmittedMdRef.current = incoming;
		hydratingRef.current = true;
		setEditorState(markdownToEditorState(incoming));
		const t = window.setTimeout(() => {
			hydratingRef.current = false;
		}, 0);
		return () => window.clearTimeout(t);
	}, [value]);

	const handleEditorStateChange = useCallback(
		(nextState) => {
			setEditorState(nextState);
			if (disabled || hydratingRef.current) return;
			const md = editorStateToMarkdown(nextState);
			lastEmittedMdRef.current = md;
			if (typeof onChange === "function") {
				onChange(md);
			}
		},
		[disabled, onChange]
	);

	return (
		<div
			className={`writing-preview__wysiwyg ${
				disabled ? "writing-preview__wysiwyg--disabled" : ""
			}`}
		>
			<Editor
				editorState={editorState}
				onEditorStateChange={handleEditorStateChange}
				toolbar={TOOLBAR}
				toolbarClassName="writing-preview__wysiwyg-toolbar"
				wrapperClassName="writing-preview__wysiwyg-wrapper"
				editorClassName="writing-preview__wysiwyg-editor"
				readOnly={disabled}
				toolbarHidden={disabled}
				placeholder="Edit content…"
			/>
		</div>
	);
}
